import React, { PropsWithChildren, RefObject, useMemo, JSX } from 'react';
import { Card } from 'reactstrap';
import { Sorter } from './Sorter';
import { MapsWrapper } from '../../Components/UI/Maps/MapsWrapper';
import useMediaQuery, { MEDIAQUERY_DEFAULTS } from '../../Hooks/useMediaQuery';
import AdditionalContent from './AdditionalContent';
import MapsMobileView from './MapMobileView';
import FilterMobileView from './FilterMobileView';
import AdditionalContentMobileView from './AdditionalContentMobileView';
import FilterDesktopView from './FilterDesktopView';
import { Container, FilterContainer, ServiceListProps } from './externals';
import ClosureDatesCollapse from '../../Components/ClosureDates/ClosureDatesSmallView/ClosureDatesCollapse';
import ClosureDates from '../../Components/ClosureDates/ClosureDatesSmallView/ClosureDates';

export function Filter<G extends { id: string }>(
  props: PropsWithChildren<
    ServiceListProps<G> & {
      hasFilters: boolean;
      hasContent: boolean;
      topContent?: React.ReactNode;
      searchWidgetContainerRef?: RefObject<HTMLDivElement | null>;
      searchWidgetChangedSize?: boolean;
      expectedOffset?: number;
      customFilterElement?: JSX.Element;
    }
  >
): JSX.Element {
  const {
    sortProps,
    mapConfig,
    filteredItemCount,
    filterProps,
    fullItemCount,
    items,
    additionalContent,
    additionalContentHeader,
    children,
    hasContent,
    hasFilters,
    useIcons,
    customListOptions,
    topContent,
    closureDates,
    searchWidgetContainerRef,
    searchWidgetChangedSize,
    expectedOffset,
    ExtendedMapMarkerPopup,
    mapRef,
    filterOverrides,
    filterLabelTranslationMappings,
    customFilterElement
  } = props;

  const matches = useMediaQuery({ type: 'min', query: MEDIAQUERY_DEFAULTS.md });

  const filtersFromApi = useMemo(() => {
    return filterProps
      ? filterProps.filter.filter(
          f =>
            f.type === 'MultipleSelectionFilter' ||
            f.type === 'NumberRangeFilter' ||
            f.type === 'GroupedMultipleSelectionFilter' ||
            f.type === 'SingleSelectionFilter'
        )
      : [];
  }, [filterProps]);

  return (
    <>
      {!matches && <>{topContent}</>}
      {additionalContent && !hasFilters && !matches && (
        <AdditionalContentMobileView
          additionalContent={additionalContent}
          additionalContentHeader={additionalContentHeader}
          hasFilters={hasFilters}
        />
      )}
      {closureDates && closureDates.length > 0 && (
        <ClosureDatesCollapse
          closureDates={closureDates}
          className={'iso__serviceList__closureDates'}
        />
      )}
      <div className="iso__serviceList__mobile-section">
        {items.length > 0 && mapConfig && !matches && (
          <MapsMobileView
            mapConfig={mapConfig}
            customListOptions={customListOptions}
            ExtendedMapMarkerPopup={ExtendedMapMarkerPopup}
            mapRef={mapRef}
          />
        )}
        {hasFilters && !matches && (
          <FilterMobileView
            filterProps={filterProps}
            hasFilters={hasFilters}
            additionalContent={additionalContent}
            filtersFromApi={filtersFromApi}
            useIcons={useIcons}
            customListOptions={customListOptions}
            filterOverrides={filterOverrides}
            filterLabelTranslationMappings={filterLabelTranslationMappings}
            showResultsCountInParenthesis={customListOptions?.showResultsCountInParenthesis}
            showResultsCount={customListOptions?.showResultsCount}
            showEmptyOptionsWhenFiltering={customListOptions?.showEmptyOptionsWhenFiltering}
            showResetButtonForFilter={customListOptions?.showResetButtonForFilter}
          />
        )}
        {items.length > 0 && sortProps && !sortProps?.hidden && (
          <Sorter
            onChange={sortProps.onChange}
            sorting={sortProps.sorting}
            sortingOptions={sortProps.sortingOptions}
            sortingType={sortProps.sortingType}
            customListOptions={customListOptions}
          />
        )}
        {items.length > 0 && customFilterElement}
      </div>
      <Container className="iso__serviceList">
        {hasContent && matches && (
          <FilterContainer className="w-25 iso__serviceList__filterContainer">
            {topContent}
            {items.length > 0 && mapConfig && (
              <Card className="mb-2 iso__serviceList__mapsWrapperContainer">
                <MapsWrapper
                  centerMap={mapConfig?.centerMap}
                  markers={mapConfig?.markers}
                  zoomMap={mapConfig?.zoomMap}
                  zoomSnap={mapConfig?.zoomSnap}
                  ignoreBounds={mapConfig?.ignoreBounds}
                  pad={mapConfig?.pad}
                  ExtendedMapMarkerPopup={ExtendedMapMarkerPopup}
                  mapRef={mapRef}
                />
              </Card>
            )}
            {hasFilters && (
              <FilterDesktopView
                filterProps={filterProps}
                filtersFromApi={filtersFromApi}
                filteredItemCount={filteredItemCount}
                fullItemCount={fullItemCount}
                useIcons={useIcons}
                searchWidgetContainerRef={searchWidgetContainerRef}
                searchWidgetChangedSize={searchWidgetChangedSize}
                expectedOffset={expectedOffset}
                filterOverrides={filterOverrides}
                filterLabelTranslationMappings={filterLabelTranslationMappings}
                showResultsCountInParenthesis={customListOptions?.showResultsCountInParenthesis}
                showResultsCount={customListOptions?.showResultsCount}
                showEmptyOptionsWhenFiltering={customListOptions?.showEmptyOptionsWhenFiltering}
                showResetButtonForFilter={customListOptions?.showResetButtonForFilter}
              />
            )}
            <AdditionalContent hasFilters={hasFilters} additionalContent={additionalContent} />
            {closureDates && closureDates.length > 0 && (
              <ClosureDates closureDates={closureDates} />
            )}
          </FilterContainer>
        )}
        {children}
      </Container>
    </>
  );
}
